import { QueryClient } from "@tanstack/react-query";
import QueryKeys from "../api/queryKeys";
import { territoryAddressCompare } from "../helpers/address";
import {
  Territory,
  TerritoryAddress,
  TerritoryRecord,
  TerritoryShareToken,
  TerritoryShareTokenListing,
  TerritoryTransfer,
} from "../types/scheduling/territory";
import { Tag, TagLinkUnlink } from "../types/scheduling/tags";

export function updateTerritoryCache(queryClient: QueryClient, territory: Territory) {
  queryClient.setQueriesData<Territory[]>({ queryKey: [QueryKeys.Territories] }, (old) => {
    if (!old) return [territory];
    if (territory.parentId) {
      // we need to update the parent's child array
      const existingParent = old.find((t) => t.id === territory.parentId);
      if (existingParent) {
        const newChildren: Territory[] = updateTerritories(existingParent.children ?? [], territory);
        const newParent: Territory = { ...existingParent, children: newChildren };
        if (old.some((t) => t.id === territory.parentId)) {
          return old.map((t) => (t.id === territory.parentId ? newParent : t));
        }
      }
    }
    return updateTerritories(old, territory);
  });
}

export function updateTerritoryTagsCache(
  queryClient: QueryClient,
  territoryIds: number[],
  linkUnlink: TagLinkUnlink,
  tags: Tag[],
) {
  const idSet = new Set(territoryIds);
  queryClient.setQueriesData<Territory[]>({ queryKey: [QueryKeys.Territories] }, (old) => {
    if (!old) return [];
    return old.map((terr) => {
      if (!idSet.has(terr.id)) return terr;
      const toUnlink = linkUnlink.unlink.filter((l) => l.terr_id === terr.id);
      const toLink = linkUnlink.link.filter((l) => l.terr_id === terr.id);
      const newTags = terr.tags.flatMap((tag) => {
        if (
          toUnlink.some(
            (tlul) => (tlul.tag_id === tag.id && !tag.global) || (tlul.global_tag_id === tag.id && tag.global),
          )
        ) {
          // we need to remove this tag
          return [];
        }
        return [tag];
      });
      // add the link tags in
      toLink.forEach((link) => {
        const tag = tags.find((t) => (t.id === link.tag_id && !t.global) || (t.id === link.global_tag_id && t.global));
        if (!tag) return;
        newTags.push(tag);
      });
      terr.tags = newTags;
      return terr;
    });
  });
}

function updateTerritories(old: Territory[], territory: Territory): Territory[] {
  if (old.some((t) => t.id === territory.id)) return old.map((t) => (t.id === territory.id ? territory : t));
  return [...old, territory];
}

export function deleteFromTerritoryCache(queryClient: QueryClient, territoryId: number) {
  queryClient.setQueriesData<Territory[]>({ queryKey: [QueryKeys.Territories] }, (old) => {
    if (!old) return [];
    if (old.some((t) => t.id === territoryId)) return old.filter((t) => t.id !== territoryId);
    // see if the id is a child
    const parent = old.find((t) => t.children?.some((c) => c.id === territoryId));
    if (parent) {
      const newChildren = parent.children?.filter((c) => c.id !== territoryId);
      const newParent: Territory = { ...parent, children: newChildren };
      return old.map((t) => (t.id === parent.id ? newParent : t));
    }
    return [];
  });
}

export function sortAndLabelAddresses(ta: TerritoryAddress[]) {
  const sorted = ta.sort(territoryAddressCompare);
  const labeled = sorted.map((a, i) => ({ ...a, pinLabel: `${i + 1}` }));
  return [...labeled];
}

export function updateTerritoryAddressCache(queryClient: QueryClient, territoryId: number, address: TerritoryAddress) {
  queryClient.setQueriesData<TerritoryAddress[]>({ queryKey: [QueryKeys.TerritoryAddresses, territoryId] }, (old) => {
    if (!old) return [address];
    if (old.some((ta) => ta.id === address.id))
      return sortAndLabelAddresses(old.map((ta) => (ta.id === address.id ? address : ta)));
    return sortAndLabelAddresses([...old, address]);
  });
}

export function deleteFromTerritoryAddressCache(queryClient: QueryClient, territoryId: number, addressId: number) {
  queryClient.setQueriesData<TerritoryAddress[]>({ queryKey: [QueryKeys.TerritoryAddresses, territoryId] }, (old) =>
    old ? old.filter((ta) => ta.id !== addressId) : [],
  );
}

export function updateTerritoryRecordCache(queryClient: QueryClient, record: TerritoryRecord) {
  queryClient.setQueriesData<TerritoryRecord[]>({ queryKey: [QueryKeys.TerritoryRecordsAll] }, (old) => {
    if (!old) return [record];
    if (old.some((tr) => tr.id === record.id)) return old.map((r) => (r.id === record.id ? record : r));
    return [...old, record];
  });

  queryClient.setQueriesData<TerritoryRecord[]>({ queryKey: [QueryKeys.TerritoryRecordsLatest] }, (old) => {
    if (!old) return [record];
    if (old.some((tr) => tr.id === record.id)) return old.map((r) => (r.id === record.id ? record : r));
    return [...old, record];
  });
}

export function addShareTokenToCache(queryClient: QueryClient, token: TerritoryShareToken) {
  queryClient.setQueryData<TerritoryShareTokenListing[]>([QueryKeys.SharedTerritoryTokens, token.territoryId], (old) =>
    old ? [...old, token] : [token],
  );
}

export function removeShareTokenFromCache(queryClient: QueryClient, tokenHash: string, territoryId: number) {
  queryClient.setQueryData<TerritoryShareTokenListing[]>([QueryKeys.SharedTerritoryTokens, territoryId], (old) =>
    old ? old.filter((t) => t.tokenHash !== tokenHash) : [],
  );
}

export function updateTerritoryTransferCache(
  queryClient: QueryClient,
  oldTransfer: TerritoryTransfer,
  newTransfer: TerritoryTransfer,
) {
  if (oldTransfer.id === 0) {
    queryClient.setQueryData<TerritoryTransfer[]>([QueryKeys.TerritoryTransfers], (old) =>
      old ? [...old, newTransfer] : [newTransfer],
    );
    return;
  }
  queryClient.setQueryData<TerritoryTransfer[]>([QueryKeys.TerritoryTransfers], (old) =>
    old ? old.map((tr) => (tr.id === newTransfer.id ? newTransfer : tr)) : [newTransfer],
  );
}

export function deleteTerritoryTransferFromCache(queryClient: QueryClient, transferId: number) {
  queryClient.setQueryData<TerritoryTransfer[]>([QueryKeys.TerritoryTransfers], (old) =>
    old ? old.filter((tr) => tr.id !== transferId) : [],
  );
}
